import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import CustomerHeader from '../../components/ui/CustomerHeader';
import AuthenticationModal from '../../components/ui/AuthenticationModal';
import CartSidebar from '../../components/ui/CartSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProductImageGallery from './components/ProductImageGallery';
import ProductInfo from './components/ProductInfo';
import ProductTabs from './components/ProductTabs';
import RelatedProducts from './components/RelatedProducts';
import Footer from '../../components/ui/Footer';

const ProductDetails = () => {
  const navigate = useNavigate();
  const cart = useCart();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCartSidebar, setShowCartSidebar] = useState(false);
  const cartItemCount = cart.totalCount;
  const setCartItemCount = () => {};
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Product data — fetched from the dashboard's storefront API. Customer ID
  // and API base URL come from build-time Vite env vars (.env.production).
  const API_BASE    = (import.meta).env.VITE_STOREFRONT_API_URL || 'https://dashboard.offloadhost.com';
  const CUSTOMER_ID = (import.meta).env.VITE_CUSTOMER_ID || '';

  const [searchParams] = useSearchParams();
  const productId = searchParams.get('id');

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  useEffect(() => {
    if (!CUSTOMER_ID || !productId) return;
    setProduct(null);
    setRelated([]);
    const ctl = new AbortController();
    fetch(`${API_BASE}/storefront/${CUSTOMER_ID}/products`, { signal: ctl.signal })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        const all = data?.products || [];
        const found = all.find((p) => String(p.id) === String(productId));
        if (!found) { setProduct(null); setRelated([]); return; }
        // Pick up to 4 related: same category first, then any other product
        // to fill the row. Excludes the current product.
        const others = all.filter((p) => String(p.id) !== String(found.id));
        const sameCat = others.filter((p) => p.category === found.category);
        const fillers = others.filter((p) => p.category !== found.category);
        const picked = [...sameCat, ...fillers].slice(0, 4);
        setRelated(picked.map((p) => ({
          id: p.id,
          name: p.name,
          brand: 'Bounderlaster',
          price: (p.sale_price_cents ?? p.price_cents) / 100,
          originalPrice: p.sale_price_cents != null ? p.price_cents / 100 : null,
          rating: p.rating_avg ?? 0,
          reviewCount: p.review_count ?? 0,
          image: p.image_url,
          alt: p.name,
          inStock: true,
          isNew: false,
        })));
        const onSale = found.sale_price_cents != null;
        const images = (Array.isArray(found.gallery) && found.gallery.length > 0)
          ? found.gallery.map((g) => ({ id: g.id, url: g.url, alt: g.alt || found.name }))
          : (found.image_url ? [{ id: 0, url: found.image_url, alt: found.name }] : []);
        setProduct({
          id: found.id,
          name: found.name,
          brand: 'Bounderlaster',
          image: (images && images[0] && images[0].url) || found.image_url || '',
          price: (onSale ? found.sale_price_cents : found.price_cents) / 100,
          originalPrice: onSale ? found.price_cents / 100 : null,
          rating: found.rating_avg ?? 0,
          reviewCount: found.review_count ?? 0,
          inStock: true,
          stockCount: 99,
          description: found.description || '',
          shortDescription: found.short_description || '',
          sku: found.sku || '',
          category: found.category || '',
          ingredients: '',
          usage: '',
          variants: Array.isArray(found.variants) ? found.variants : [],
          features: [],
          images,
        });
      })
      .catch((err) => { if (err?.name !== 'AbortError') console.error('product fetch failed:', err); });
    return () => ctl.abort();
  }, [productId]);

  useEffect(() => {
    // Check authentication status
    const authStatus = localStorage.getItem('isAuthenticated');
    setIsAuthenticated(authStatus === 'true');

    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  const handleAuthClick = () => {
    setShowAuthModal(true);
  };

  const handleAuthenticate = (authData) => {
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('userData', JSON.stringify(authData?.userData));
    showSuccessNotification('Successfully signed in!');
  };

  const handleCartClick = () => {
    setShowCartSidebar(true);
  };

  const handleAddToCart = (productData) => {
    const qty = Math.max(1, Number(productData?.quantity) || 1);
    cart.addItem(productData, qty);
    showSuccessNotification(`Added ${productData?.name} to cart!`);
  };

  const handleAddToWishlist = (productData) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    showSuccessNotification(`Added ${productData?.name} to wishlist!`);
  };

  const handleRelatedProductAddToCart = (productData) => {
    cart.addItem(productData, 1);
    showSuccessNotification(`Added ${productData?.name} to cart!`);
  };

  // Add this block - handlers for cart operations
  const handleUpdateQuantity = (itemId, newQuantity) => {
    // Update cart item quantity logic
    console.log(`Updating item ${itemId} to quantity ${newQuantity}`);
  };

  const handleRemoveItem = (itemId) => {
    // Remove item from cart logic
    setCartItemCount((prev) => Math.max(0, prev - 1));
    showSuccessNotification('Item removed from cart');
  };
  // End of added block

  const showSuccessNotification = (message) => {
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <CustomerHeader
        cartItemCount={cartItemCount}
        onCartClick={() => setShowCartSidebar(true)}
      />

      <div className="pt-16 flex-1">
        {/* Breadcrumb */}
        <div className="bg-muted/30 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center space-x-2 text-sm">
              <Link to="/product-catalog" className="text-muted-foreground hover:text-foreground transition-colors">
                Products
              </Link>
              <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
              {product?.category && (
                <>
                  <Link to="/product-catalog" className="text-muted-foreground hover:text-foreground transition-colors">
                    {product.category}
                  </Link>
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                </>
              )}
              <span className="text-foreground font-medium">{product?.name || ''}</span>
            </nav>
          </div>
        </div>

        {/* Product Details */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Images */}
            <div className="lg:sticky lg:top-24 lg:self-start">
              <ProductImageGallery
                images={product?.images}
                productName={product?.name} />

            </div>

            {/* Product Information */}
            <div>
              {product && <ProductInfo
              key={productId}
                product={product}
                onAddToCart={handleAddToCart}
                onAddToWishlist={handleAddToWishlist} />}

            </div>
          </div>

          {/* Product Details Tabs */}
          <div className="mt-16">
            <ProductTabs key={productId} product={product} />
          </div>

          {/* Related Products */}
          <div className="mt-16">
            <RelatedProducts key={productId} products={related} onAddToCart={handleRelatedProductAddToCart} />
          </div>
        </div>

        {/* Back to Top Button */}
        <div className="fixed bottom-6 right-6 z-30">
          <Button
            variant="outline"
            size="icon"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="w-12 h-12 rounded-full shadow-elevation-2 bg-background/80 backdrop-blur-sm">

            <Icon name="ArrowUp" size={20} />
          </Button>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Success Notification */}
      {showNotification &&
      <div className="fixed top-20 right-4 z-50 bg-success text-success-foreground px-4 py-3 rounded-lg shadow-elevation-3 flex items-center space-x-2 animate-in slide-in-from-right">
          <Icon name="CheckCircle" size={20} />
          <span className="text-sm font-medium">{notificationMessage}</span>
        </div>
      }
      {/* Authentication Modal */}
      <AuthenticationModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthenticate={handleAuthenticate}
        initialMode="login" />

      {/* Cart Sidebar */}
      <CartSidebar
        isOpen={showCartSidebar}
        onClose={() => setShowCartSidebar(false)}
        onCheckout={() => navigate('/shopping-cart')}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem} />

    </div>
  );
};

export default ProductDetails;